import { Component, JSXElement, Show } from 'solid-js';
import { DATA_THEME_ATTRIBUTE, DEFAULT_MODUS_THEME } from '../constants';
import type { IconColor } from '../Icon/icon-color';
import Icon from '../Icon/Icon';
import type { IconSize } from '../Icon/icon-size';

export type TreeItemSize = 'sm' | 'md' | 'lg';
export type TreeItemTooltipPosition =
  | 'auto'
  | 'top'
  | 'right'
  | 'bottom'
  | 'left';

export interface TreeItemProps {
  label?: string;
  value?: string;
  selected?: boolean;
  /** Renders a visual focus ring. */
  focused?: boolean;
  disabled?: boolean;
  /** Renders a checkbox at the start of the row. */
  checkbox?: boolean;
  /** Renders top/bottom borders. */
  bordered?: boolean;
  size?: TreeItemSize;
  /** Secondary line of text beneath the label. */
  subLabel?: string;
  tooltipContent?: string;
  tooltipPosition?: TreeItemTooltipPosition;
  /**
   * Displays a caret and either toggles the nested `<modus-wc-tree-menu>`
   * automatically, OR (when `blockExpand` is also true) emits `itemSelect`
   * so the consumer can drive expansion externally.
   */
  hasSubmenu?: boolean;
  /**
   * When true (and `hasSubmenu` is true), suppresses the built-in toggle
   * and only emits `itemSelect` — lets the consumer fully control
   * loading/expanding nested folders (used by tcweb FolderTree).
   */
  blockExpand?: boolean;
  /** Applied to the underlying `<li>`. */
  customClass?: string;
  /** Icon CSS class projected into the `slot="start"` slot (e.g. `"tc-icon-user"`). */
  startIcon?: string;
  /** Icon CSS class projected into the `slot="end"` slot (e.g. `"tc-icon-launch"`). */
  endIcon?: string;
  /** Overrides menu/item size for the start icon only. */
  startIconSize?: IconSize;
  /** Overrides menu/item size for the end icon only. */
  endIconSize?: IconSize;
  /** When set, end-icon click invokes this instead of re-triggering row selection. */
  onEndIconSelect?(): void;
  /** Color applied to both start and end icons. */
  iconColor?: IconColor;
  /** When true, renders an `<hr>` separator below the item. */
  dividerAfter?: boolean;
  /** Projects nested `<modus-wc-tree-menu>`s. */
  children?: JSXElement;
}

const treeSizeToIconSize = (size: TreeItemSize | undefined): IconSize => {
  switch (size) {
    case 'sm':
      return 'icon-small';
    case 'lg':
      return 'icon-large';
    default:
      return 'icon-medium';
  }
};

const TreeItem: Component<TreeItemProps> = (props): JSXElement => {
  const getDataTheme = (): string =>
    (typeof document !== 'undefined' &&
      document.documentElement.getAttribute(DATA_THEME_ATTRIBUTE)) ||
    DEFAULT_MODUS_THEME;

  const defaultIconSize = (): IconSize => treeSizeToIconSize(props.size);
  const startIconSize = (): IconSize =>
    props.startIconSize ?? defaultIconSize();
  const endIconSize = (): IconSize => props.endIconSize ?? defaultIconSize();

  const handleEndIconClick = (e: MouseEvent): void => {
    e.stopPropagation();
    if (props.onEndIconSelect) {
      props.onEndIconSelect();
      return;
    }
    const treeItemEl = (e.currentTarget as HTMLElement).closest(
      'modus-wc-tree-item'
    );
    const interactive = treeItemEl?.querySelector(
      '.modus-wc-menu-item-interactive'
    ) as HTMLElement | null;
    interactive?.click();
  };

  return (
    <div data-theme={getDataTheme()} style={{ display: 'contents' }}>
      <modus-wc-tree-item
        label={props.label ?? ''}
        value={props.value ?? ''}
        selected={props.selected ?? false}
        focused={props.focused ?? false}
        disabled={props.disabled ?? false}
        checkbox={props.checkbox ?? false}
        bordered={props.bordered ?? false}
        size={props.size ?? 'md'}
        sub-label={props.subLabel ?? ''}
        tooltip-content={props.tooltipContent ?? ''}
        tooltip-position={props.tooltipPosition ?? 'auto'}
        has-submenu={props.hasSubmenu ?? false}
        block-expand={props.blockExpand ?? false}
        custom-class={props.customClass ?? ''}
      >
        <Show when={props.startIcon}>
          <span slot="start">
            <Icon
              icon={props.startIcon as string}
              size={startIconSize()}
              color={props.iconColor}
            />
          </span>
        </Show>

        {props.children}

        <Show when={props.endIcon}>
          <span slot="end" onClick={handleEndIconClick}>
            <Icon
              icon={props.endIcon as string}
              size={endIconSize()}
              color={props.iconColor}
            />
          </span>
        </Show>
      </modus-wc-tree-item>
    </div>
  );
};

export default TreeItem;
